import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { motion } from 'framer-motion';
import { ArrowDown, ShieldCheck } from 'lucide-react';

const scrollCtaDef: SlotGroupDefinition = {
  label: 'Scroll CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '#al-one-content' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore our approach' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const NODES = [
  { x: 12, y: 22, d: 0 },
  { x: 28, y: 64, d: 0.6 },
  { x: 46, y: 30, d: 1.2 },
  { x: 63, y: 72, d: 0.3 },
  { x: 78, y: 26, d: 0.9 },
  { x: 90, y: 58, d: 1.5 },
] as const;

const LINKS = [
  [0, 2],
  [1, 2],
  [2, 3],
  [2, 4],
  [3, 5],
  [4, 5],
] as const;

function DataNetwork() {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      {LINKS.map(([a, b], i) => (
        <motion.line
          key={`${a}-${b}`}
          x1={NODES[a].x}
          y1={NODES[a].y}
          x2={NODES[b].x}
          y2={NODES[b].y}
          className="stroke-[rgb(var(--ala-secondary))]"
          strokeWidth={0.12}
          vectorEffect="non-scaling-stroke"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.35 }}
          transition={{ duration: 1.6, delay: 0.4 + i * 0.15, ease: 'easeOut' }}
        />
      ))}
      {NODES.map((node, i) => (
        <motion.circle
          key={i}
          cx={node.x}
          cy={node.y}
          r={0.5}
          className="fill-[rgb(var(--ala-secondary))]"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 3, delay: node.d, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </svg>
  );
}

export function AlOneHero() {
  const scrollCta = useSlotGroup('alonehero.scroll_cta', scrollCtaDef);

  return (
    <section className="relative flex min-h-[min(88svh,820px)] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))] md:min-h-[820px]">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse at 70% 40%, black 20%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 70% 40%, black 20%, transparent 75%)',
          }}
        />
        <motion.div
          className="absolute -right-[10%] top-[5%] h-[520px] w-[520px] rounded-full bg-[rgb(var(--ala-secondary))]/30 blur-[140px]"
          animate={{ x: [0, -60, 0], y: [0, 40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-[-15%] left-[20%] h-[420px] w-[420px] rounded-full bg-[rgb(var(--ala-accent))]/20 blur-[140px]"
          animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <DataNetwork />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgb(var(--ala-dark))] via-[rgb(var(--ala-dark))]/40 to-transparent" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative z-10 mx-auto flex w-full max-w-[1440px] flex-col gap-6 px-6 pb-14 pt-40 md:px-[clamp(16px,6vw,87px)] md:pb-[clamp(40px,6vw,96px)]"
      >
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="alonehero.eyebrow"
              label="Eyebrow"
              as="span"
              className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
            />
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--ala-secondary))]/40 bg-[rgb(var(--ala-secondary))]/10 px-3 py-1 backdrop-blur-sm">
            <ShieldCheck className="h-4 w-4 text-[rgb(var(--ala-secondary))]" aria-hidden />
            <RichTextContent
              path="alonehero.badge"
              label="Badge"
              as="span"
              className="font-heading text-xs font-semibold uppercase tracking-[0.14em] text-[rgb(var(--ala-on-primary))]"
            />
          </div>
        </div>

        <RichTextContent
          path="alonehero.title"
          label="Title"
          as="h1"
          className="bg-gradient-to-r from-white via-white to-[rgb(var(--ala-secondary))] bg-clip-text max-w-[1100px] font-heading text-[clamp(52px,8vw,128px)] font-bold leading-[1] tracking-[-0.04em] pb-[0.12em] text-transparent [&_p]:text-transparent"
        />

        <RichTextContent
          path="alonehero.subtitle"
          label="Subtitle"
          as="p"
          className="max-w-[720px] font-heading text-[clamp(18px,1.6vw,24px)] font-normal leading-[1.45] text-[rgb(var(--ala-on-primary))]/85 [&_p]:text-[rgb(var(--ala-on-primary))]/85"
        />

        <EditableSlotGroup groupPath="alonehero.scroll_cta" definition={scrollCtaDef}>
          <a
            href={scrollCta.url || '#al-one-content'}
            target={scrollCta._target || '_self'}
            className="group mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]"
          >
            <span>{scrollCta.label || 'Explore our approach'}</span>
            <ArrowDown className="h-5 w-5 transition-transform group-hover:translate-y-0.5" />
          </a>
        </EditableSlotGroup>
      </motion.div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AlOneHero',
  displayName: 'AL-ONE Hero',
  tokens: {
    'alonehero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Technology &amp; AI at ALA</p>',
    },
    'alonehero.badge': {
      label: 'Badge',
      type: 'richtext',
      defaultValue: '<p>AL-ONE · Patent Pending</p>',
    },
    'alonehero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Our Tech Advantage</p>',
    },
    'alonehero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>How American Landmark puts technology and auditable AI to work — anchored by AL-ONE, our proprietary, in-house asset intelligence platform.</p>',
    },
  },
  slotGroups: {
    'alonehero.scroll_cta': scrollCtaDef,
  },
};

(AlOneHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AlOneHero;
